/**
 * 选区运算（M2-06 / DESIGN.md §5.1、§6.2「选区运算 / 羽化 / 扩展收缩 / 边界」）。
 *
 * 所有运算接受不可变蒙版、返回新蒙版。逐像素语义：
 * - 并（加选）  max(a, b)
 * - 减（减选）  min(a, 255 − b)
 * - 交         min(a, b)
 * - 反选       255 − a（实现上只翻 base 与已有块，O(内容) 而非 O(文档)）
 *
 * 羽化是**可分离高斯**：σ = r / 1.2816 的一半…精确地说，取
 * σ = 2r / 2.5631，使理想阶跃边缘的 10%→90% 过渡宽度恰为 2r
 * （高斯阶跃响应是 Φ(d/σ)，Φ⁻¹(0.9) − Φ⁻¹(0.1) = 2.5631）。
 *
 * 扩展/收缩是方形结构元的形态学 max/min 滤波（可分离）；
 * 边界 = 扩展 ⌈w/2⌉ 减去 收缩 ⌊w/2⌋，得到以原边缘为中心、宽 w 的环。
 *
 * 耗时操作（羽化/扩展/收缩，O(像素×核)）接受 AbortSignal；
 * 输入不可变，取消只意味着**没有输出**，绝不会有半成品。
 */
import { TILE } from '../tile/key';
import { SelectionMask } from './mask';
import { magicWand, type SourceImage } from './magic-wand';
import { oklab_distance_srgb8 } from '../../wasm/core/photosmith_core';
import { ensureKernel } from '../wasm-kernel';

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
}

function assertSameSize(a: SelectionMask, b: SelectionMask): void {
  if (a.width !== b.width || a.height !== b.height) {
    throw new RangeError(
      `选区尺寸不一致：${a.width}×${a.height} 与 ${b.width}×${b.height}。`,
    );
  }
}

/* ------------------------------------------------------------------ */
/* 布尔运算                                                             */
/* ------------------------------------------------------------------ */

type PixelOp = (a: number, b: number) => number;

/**
 * 逐像素合并。只在「任一侧偏离其 base」的块上工作，
 * 其余块的结果是常数（resultBase），根本不分配。
 */
function combine(a: SelectionMask, b: SelectionMask, op: PixelOp): SelectionMask {
  assertSameSize(a, b);
  // 两个 base 都是 0/255，任何 PixelOp 作用其上仍是 0/255
  const base: 0 | 255 = op(a.base, b.base) >= 128 ? 255 : 0;

  const ids = new Set<string>();
  for (const { tx, ty } of a.tileCoords()) ids.add(`${tx} ${ty}`);
  for (const { tx, ty } of b.tileCoords()) ids.add(`${tx} ${ty}`);

  const tiles = new Map<string, Uint8Array>();
  for (const id of ids) {
    const [tx, ty] = id.split(' ').map(Number) as [number, number];
    const ta = a.tile(tx, ty);
    const tb = b.tile(tx, ty);
    const out = new Uint8Array(TILE * TILE);
    for (let i = 0; i < out.length; i++) {
      out[i] = op(ta === null ? a.base : ta[i]!, tb === null ? b.base : tb[i]!);
    }
    tiles.set(id, out);
  }
  return SelectionMask.build(a.width, a.height, base, tiles);
}

/** 并集（Shift 加选）。 */
export function union(a: SelectionMask, b: SelectionMask): SelectionMask {
  return combine(a, b, (x, y) => Math.max(x, y));
}

/** 交集。 */
export function intersect(a: SelectionMask, b: SelectionMask): SelectionMask {
  return combine(a, b, (x, y) => Math.min(x, y));
}

/** 差集（Alt 减选）：从 a 里去掉 b。 */
export function subtract(a: SelectionMask, b: SelectionMask): SelectionMask {
  return combine(a, b, (x, y) => Math.min(x, 255 - y));
}

/** 反选。O(已分配块)，与文档尺寸无关。 */
export function invert(a: SelectionMask): SelectionMask {
  const base: 0 | 255 = a.base === 0 ? 255 : 0;
  const tiles = new Map<string, Uint8Array>();
  for (const { tx, ty } of a.tileCoords()) {
    const src = a.tile(tx, ty)!;
    const out = new Uint8Array(TILE * TILE);
    for (let i = 0; i < out.length; i++) out[i] = 255 - src[i]!;
    tiles.set(`${tx} ${ty}`, out);
  }
  return SelectionMask.build(a.width, a.height, base, tiles);
}

/* ------------------------------------------------------------------ */
/* 几何选区                                                             */
/* ------------------------------------------------------------------ */

export interface RectSpec {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** 把一个覆盖率函数栅格化成蒙版。coverage 返回 [0,1]。 */
function rasterize(
  width: number,
  height: number,
  bounds: RectSpec,
  coverage: (x: number, y: number) => number,
): SelectionMask {
  const tiles = new Map<string, Uint8Array>();
  const tx0 = Math.max(0, Math.floor(bounds.x / TILE));
  const ty0 = Math.max(0, Math.floor(bounds.y / TILE));
  const tx1 = Math.min(Math.ceil(width / TILE) - 1, Math.floor((bounds.x + bounds.width) / TILE));
  const ty1 = Math.min(
    Math.ceil(height / TILE) - 1,
    Math.floor((bounds.y + bounds.height) / TILE),
  );

  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      const data = new Uint8Array(TILE * TILE);
      let any = false;
      const maxY = Math.min(TILE, height - ty * TILE);
      const maxX = Math.min(TILE, width - tx * TILE);
      for (let y = 0; y < maxY; y++) {
        for (let x = 0; x < maxX; x++) {
          const value = Math.round(coverage(tx * TILE + x, ty * TILE + y) * 255);
          if (value > 0) {
            data[y * TILE + x] = value;
            any = true;
          }
        }
      }
      if (any) tiles.set(`${tx} ${ty}`, data);
    }
  }
  return SelectionMask.build(width, height, 0, tiles);
}

/** 矩形选区。整数边界硬边，小数边界按覆盖率抗锯齿。 */
export function fromRect(width: number, height: number, rect: RectSpec): SelectionMask {
  const x1 = rect.x + rect.width;
  const y1 = rect.y + rect.height;
  return rasterize(width, height, rect, (px, py) => {
    // 像素 [px, px+1) 与矩形在两个轴上的覆盖长度之积
    const cx = Math.min(px + 1, x1) - Math.max(px, rect.x);
    const cy = Math.min(py + 1, y1) - Math.max(py, rect.y);
    return Math.max(0, Math.min(1, cx)) * Math.max(0, Math.min(1, cy));
  });
}

/**
 * 圆角矩形选区（矩形选框工具的"半径"选项，参照 Photoshop）。半径会先
 * 夹到 `min(width, height) / 2`——半径超过短边一半时四个圆弧会互相
 * 咬穿，夹住之后退化成"两端是半圆的胶囊/圆形"，而不是产出自相矛盾的
 * 形状。半径 ≤ 0 时退化为普通矩形（直接复用 `fromRect`，不用为这个
 * 边界情形走一遍更贵的 4×4 超采样）。
 *
 * 判内外用"钳制到内缩圆心矩形后的距离"这个经典写法：把采样点各轴分别
 * 钳制到 `[边 + r, 对边 - r]`，得到的点是圆角矩形"核心矩形"上离采样点
 * 最近的点；采样点到这个点的距离 ≤ r 就算在里面——直边区域钳制结果
 * 就是采样点自己（距离恒为 0，必然在里面），只有四个角的区域才会真的
 * 钳到核心矩形的角上，退化成"到最近圆心的距离"这条圆弧判定，两种情形
 * 用同一个式子表达，不用分四个角单独判断。
 */
export function fromRoundedRect(
  width: number,
  height: number,
  rect: RectSpec,
  radius: number,
): SelectionMask {
  if (radius <= 0) return fromRect(width, height, rect);
  const r = Math.min(radius, rect.width / 2, rect.height / 2);
  const left = rect.x;
  const top = rect.y;
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  const SAMPLES = 4;
  return rasterize(width, height, rect, (px, py) => {
    let inside = 0;
    for (let sy = 0; sy < SAMPLES; sy++) {
      for (let sx = 0; sx < SAMPLES; sx++) {
        const x = px + (sx + 0.5) / SAMPLES;
        const y = py + (sy + 0.5) / SAMPLES;
        const cx = Math.min(Math.max(x, left + r), right - r);
        const cy = Math.min(Math.max(y, top + r), bottom - r);
        const dx = x - cx;
        const dy = y - cy;
        if (dx * dx + dy * dy <= r * r) inside++;
      }
    }
    return inside / (SAMPLES * SAMPLES);
  });
}

/**
 * 从一份已合成好的像素缓冲里的某个通道建立选区（图层缩略图/通道面板的
 * Cmd/Ctrl+点击转选区用，DESIGN.md §7.1 补充）——是**软选区**：通道的字节
 * 值本身就是选中程度（0–255 直接映射到蒙版 0–255），不是二值化阈值。
 * `channelOffset`/`stride` 让同一个函数既能喂 RGBA（取 alpha：offset=3,
 * stride=4）也能喂单通道灰度缓冲（offset=0, stride=1）。
 */
export function fromChannel(
  width: number,
  height: number,
  pixels: Uint8Array,
  channelOffset: number,
  stride: number,
): SelectionMask {
  return rasterize(width, height, { x: 0, y: 0, width, height }, (x, y) => {
    const value = pixels[(y * width + x) * stride + channelOffset];
    return value === undefined ? 0 : value / 255;
  });
}

/** 椭圆选区（内切于给定矩形），4×4 超采样抗锯齿。 */
export function fromEllipse(width: number, height: number, rect: RectSpec): SelectionMask {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const rx = rect.width / 2;
  const ry = rect.height / 2;
  if (rx <= 0 || ry <= 0) return SelectionMask.empty(width, height);
  const SAMPLES = 4;
  return rasterize(width, height, rect, (px, py) => {
    let inside = 0;
    for (let sy = 0; sy < SAMPLES; sy++) {
      for (let sx = 0; sx < SAMPLES; sx++) {
        const dx = (px + (sx + 0.5) / SAMPLES - cx) / rx;
        const dy = (py + (sy + 0.5) / SAMPLES - cy) / ry;
        if (dx * dx + dy * dy <= 1) inside++;
      }
    }
    return inside / (SAMPLES * SAMPLES);
  });
}

/**
 * 多边形选区（套索/多边形套索）。顶点为文档空间坐标，自动闭合。
 *
 * 抗锯齿：每像素行取 4 条子扫描线，与多边形边求交得到覆盖区间，
 * 区间端点按小数位置对像素做部分覆盖累加。even-odd 填充规则。
 */
export function fromPolygon(
  width: number,
  height: number,
  points: ReadonlyArray<{ readonly x: number; readonly y: number }>,
): SelectionMask {
  if (points.length < 3) return SelectionMask.empty(width, height);

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const y0 = Math.max(0, Math.floor(minY));
  const y1 = Math.min(height, Math.ceil(maxY));
  const x0 = Math.max(0, Math.floor(minX));
  const x1 = Math.min(width, Math.ceil(maxX));
  if (y0 >= y1 || x0 >= x1) return SelectionMask.empty(width, height);

  const rowWidth = x1 - x0;
  const coverage = new Float32Array(rowWidth);
  const row = new Uint8Array(rowWidth);
  const writer = new TileRowWriter(0, x0);
  const SUB = 4;

  for (let y = y0; y < y1; y++) {
    coverage.fill(0);
    for (let sub = 0; sub < SUB; sub++) {
      const sy = y + (sub + 0.5) / SUB;
      // 与每条边求交（even-odd：收集全部交点排序后成对填充）
      const xs: number[] = [];
      for (let i = 0; i < points.length; i++) {
        const a = points[i]!;
        const b = points[(i + 1) % points.length]!;
        if (a.y === b.y) continue;
        if ((sy >= a.y && sy < b.y) || (sy >= b.y && sy < a.y)) {
          xs.push(a.x + ((sy - a.y) / (b.y - a.y)) * (b.x - a.x));
        }
      }
      xs.sort((p, q) => p - q);
      for (let i = 0; i + 1 < xs.length; i += 2) {
        const from = Math.max(x0, xs[i]!);
        const to = Math.min(x1, xs[i + 1]!);
        if (from >= to) continue;
        // 区间端点的部分像素覆盖
        const first = Math.floor(from);
        const last = Math.ceil(to) - 1;
        for (let px = first; px <= last; px++) {
          const overlap = Math.min(px + 1, to) - Math.max(px, from);
          if (overlap > 0) coverage[px - x0] = coverage[px - x0]! + overlap / SUB;
        }
      }
    }
    for (let i = 0; i < rowWidth; i++) {
      row[i] = Math.round(Math.min(1, coverage[i]!) * 255);
    }
    writer.writeRow(y, row);
  }
  return writer.build(SelectionMask.empty(width, height));
}

/* ------------------------------------------------------------------ */
/* 流式分离滤波：羽化与形态学共用的行机制                                   */
/* ------------------------------------------------------------------ */
/*
 * 不物化整图工作面（§4.3 不变量，test/engine/tile/store.test.ts 的扫描守着）：
 * 全选状态下羽化一个 10000² 文档，O(W×H) 的浮点中间缓冲就是 800MB。
 * 分离滤波只需要 2K+1 行的历史，用环形行缓冲把内存压到 O(W×K)。
 *
 * 边界语义：**文档边界外复制边缘值**（贴边的选区羽化后仍贴边，与 PS 一致）；
 * 工作区域边界外但仍在文档内的值就是 base（区域已含 margin），无需特判。
 */

interface Region {
  readonly x0: number;
  readonly y0: number;
  /** 半开。 */
  readonly x1: number;
  readonly y1: number;
}

function regionFor(mask: SelectionMask, margin: number): Region | null {
  const bounds = mask.contentBounds();
  if (bounds === null) return null;
  return {
    x0: Math.max(0, bounds.x - margin),
    y0: Math.max(0, bounds.y - margin),
    x1: Math.min(mask.width, bounds.x + bounds.width + margin),
    y1: Math.min(mask.height, bounds.y + bounds.height + margin),
  };
}

/** 输出行 → 分块蒙版。写到哪块分配到哪块。 */
class TileRowWriter {
  readonly #tiles = new Map<string, Uint8Array>();

  constructor(
    private readonly base: 0 | 255,
    private readonly x0: number,
  ) {}

  writeRow(y: number, row: Uint8Array): void {
    const ty = Math.floor(y / TILE);
    const rowOffset = (y % TILE) * TILE;
    let i = 0;
    while (i < row.length) {
      const docX = this.x0 + i;
      const tx = Math.floor(docX / TILE);
      const segment = Math.min((tx + 1) * TILE - docX, row.length - i);
      const id = `${tx} ${ty}`;
      let tile = this.#tiles.get(id);
      if (tile === undefined) {
        tile = new Uint8Array(TILE * TILE).fill(this.base);
        this.#tiles.set(id, tile);
      }
      tile.set(row.subarray(i, i + segment), rowOffset + (docX % TILE));
      i += segment;
    }
  }

  build(mask: SelectionMask): SelectionMask {
    return SelectionMask.build(mask.width, mask.height, mask.base, this.#tiles);
  }
}

/**
 * 读一行并做水平方向的边界扩展，写入长度 (x1−x0)+2·pad 的 out。
 * 超出文档的部分复制文档边缘值。
 */
function readExtendedRow(
  mask: SelectionMask,
  region: Region,
  pad: number,
  y: number,
  out: Uint8Array,
): void {
  const start = region.x0 - pad;
  mask.readRow(y, start, region.x1 + pad, out);
  // readRow 对文档外填了 base，这里改成复制边缘（PS 语义：贴边不衰减）
  const firstInDoc = Math.max(0, start) - start;
  if (firstInDoc > 0 && firstInDoc < out.length) out.fill(out[firstInDoc]!, 0, firstInDoc);
  const lastInDoc = Math.min(mask.width, region.x1 + pad) - start;
  if (lastInDoc > 0 && lastInDoc < out.length) out.fill(out[lastInDoc - 1]!, lastInDoc);
}

/**
 * 流式分离滤波骨架。
 *
 * @param horizontal 对扩展行做水平滤波：`(ext, dest)`，dest 长度 = 区域宽。
 * @param vertical   垂直合并环内 2K+1 行：`(rows, x) => 输出值`，rows 按核序排列。
 */
function separableStreaming(
  mask: SelectionMask,
  radius: number,
  makeRow: () => Float32Array | Uint8Array,
  horizontal: (ext: Uint8Array, dest: Float32Array | Uint8Array) => void,
  vertical: (rows: ReadonlyArray<Float32Array | Uint8Array>, x: number) => number,
  options: OpOptions,
): SelectionMask {
  throwIfAborted(options.signal);
  const region = regionFor(mask, radius);
  if (region === null) return mask; // 空/全选：没有边缘可处理

  const width = region.x1 - region.x0;
  const ringSize = radius * 2 + 1;
  const ring = Array.from({ length: ringSize }, makeRow);
  const ringYs = new Array<number>(ringSize).fill(Number.NaN);
  const ext = new Uint8Array(width + radius * 2);
  const outRow = new Uint8Array(width);
  const windowRows = new Array<Float32Array | Uint8Array>(ringSize);
  const writer = new TileRowWriter(mask.base, region.x0);

  const fillSlot = (ys: number): void => {
    const slot = ((ys % ringSize) + ringSize) % ringSize;
    if (ringYs[slot] === ys) return;
    ringYs[slot] = ys;
    // 文档外复制边缘行；区域外（文档内）是 base 常数行——直接读也正确且更简单
    const clamped = Math.min(mask.height - 1, Math.max(0, ys));
    readExtendedRow(mask, region, radius, clamped, ext);
    horizontal(ext, ring[slot]!);
  };

  for (let y = region.y0; y < region.y1; y++) {
    if ((y - region.y0) % 64 === 0) throwIfAborted(options.signal);
    if (y === region.y0) {
      for (let ys = y - radius; ys <= y + radius; ys++) fillSlot(ys);
    } else {
      fillSlot(y + radius);
    }
    for (let k = -radius; k <= radius; k++) {
      const ys = y + k;
      windowRows[k + radius] = ring[((ys % ringSize) + ringSize) % ringSize]!;
    }
    for (let x = 0; x < width; x++) outRow[x] = vertical(windowRows, x);
    writer.writeRow(y, outRow);
  }
  return writer.build(mask);
}

export interface OpOptions {
  readonly signal?: AbortSignal;
}

/* ------------------------------------------------------------------ */
/* 羽化                                                                 */
/* ------------------------------------------------------------------ */

/** 10%→90% 过渡宽度为 2r 时的 σ 换算系数：2 / (Φ⁻¹(0.9) − Φ⁻¹(0.1))。 */
const SIGMA_PER_RADIUS = 2 / 2.5631;

/** 可分离高斯羽化。radius 的含义见文件头（边缘过渡宽度 ≈ 2×radius）。 */
export function feather(
  mask: SelectionMask,
  radius: number,
  options: OpOptions = {},
): SelectionMask {
  if (radius <= 0) return mask;
  const sigma = radius * SIGMA_PER_RADIUS;
  const kernelRadius = Math.max(1, Math.ceil(sigma * 3));

  const kernel = new Float32Array(kernelRadius * 2 + 1);
  let sum = 0;
  for (let i = -kernelRadius; i <= kernelRadius; i++) {
    const w = Math.exp(-(i * i) / (2 * sigma * sigma));
    kernel[i + kernelRadius] = w;
    sum += w;
  }
  for (let i = 0; i < kernel.length; i++) kernel[i] = kernel[i]! / sum;

  return separableStreaming(
    mask,
    kernelRadius,
    () => new Float32Array(mask.width), // 行缓冲按最大可能宽度分配一次
    (ext, dest) => {
      const n = ext.length - kernelRadius * 2;
      for (let x = 0; x < n; x++) {
        let acc = 0;
        for (let k = 0; k < kernel.length; k++) acc += ext[x + k]! * kernel[k]!;
        (dest as Float32Array)[x] = acc;
      }
    },
    (rows, x) => {
      let acc = 0;
      for (let k = 0; k < kernel.length; k++) acc += (rows[k] as Float32Array)[x]! * kernel[k]!;
      return Math.round(acc);
    },
    options,
  );
}

/* ------------------------------------------------------------------ */
/* 扩展 / 收缩 / 边界                                                    */
/* ------------------------------------------------------------------ */

/** 可分离的形态学滤波（方形结构元）。pick 是 Math.max（扩展）或 Math.min（收缩）。 */
function morph(
  mask: SelectionMask,
  radius: number,
  pick: (a: number, b: number) => number,
  options: OpOptions,
): SelectionMask {
  if (radius <= 0) return mask;
  return separableStreaming(
    mask,
    radius,
    () => new Uint8Array(mask.width),
    (ext, dest) => {
      const n = ext.length - radius * 2;
      for (let x = 0; x < n; x++) {
        let value = ext[x + radius]!;
        for (let k = 0; k <= radius * 2; k++) value = pick(value, ext[x + k]!);
        (dest as Uint8Array)[x] = value;
      }
    },
    (rows, x) => {
      let value = (rows[radius] as Uint8Array)[x]!;
      for (let k = 0; k < rows.length; k++) value = pick(value, (rows[k] as Uint8Array)[x]!);
      return value;
    },
    options,
  );
}

/** 扩展选区 radius 像素。 */
export function expand(
  mask: SelectionMask,
  radius: number,
  options: OpOptions = {},
): SelectionMask {
  return morph(mask, radius, Math.max, options);
}

/** 收缩选区 radius 像素。 */
export function contract(
  mask: SelectionMask,
  radius: number,
  options: OpOptions = {},
): SelectionMask {
  return morph(mask, radius, Math.min, options);
}

/** 边界：以原边缘为中心、宽约 width 像素的环。 */
export function border(
  mask: SelectionMask,
  width: number,
  options: OpOptions = {},
): SelectionMask {
  throwIfAborted(options.signal);
  const out = Math.ceil(width / 2);
  const inner = Math.floor(width / 2);
  return subtract(expand(mask, out, options), contract(mask, inner, options));
}

/**
 * 平滑选区：抹平小凹角与小凸起的毛刺。
 *
 * PS 的「平滑」内部是排名/众数滤波，这里用等价形态学近似——
 * 先「闭」（expand 再 contract）填掉小凹角/小洞，再「开」
 * （contract 再 expand）削掉小凸起——对 radius 级别以下的锯齿
 * 效果与真实实现无法区分，只是数学表述不同。
 */
export function smooth(
  mask: SelectionMask,
  radius: number,
  options: OpOptions = {},
): SelectionMask {
  throwIfAborted(options.signal);
  const closed = contract(expand(mask, radius, options), radius, options);
  return expand(contract(closed, radius, options), radius, options);
}

/* ------------------------------------------------------------------ */
/* 扩大选区 / 选取相似（对比 Photoshop 补全第一档缺口 阶段 2）             */
/* ------------------------------------------------------------------ */

/**
 * 在已分配块里找一个**确定被选中**（值 > 127）的像素，当作这份选区的
 * 代表色采样点。选 bbox 中心有可能落在未选中的凹陷处（选区不是矩形时），
 * 所以直接扫已分配块找一个真正选中的点，而不是猜几何中心。
 *
 * base=255（"全选去掉几个洞"）且所有已分配块都没有 >127 的像素时，退化
 * 成猜文档几何中心——这种形状（洞比实际选区还大）本来就是这次"选取相似"
 * 单一代表色简化版覆盖不到的边界情形，不专门处理。
 */
function representativeSeed(mask: SelectionMask): { x: number; y: number } | null {
  for (const { tx, ty } of mask.tileCoords()) {
    const tile = mask.tile(tx, ty);
    if (tile === null) continue;
    for (let i = 0; i < tile.length; i++) {
      if (tile[i]! > 127) {
        return { x: tx * TILE + (i % TILE), y: ty * TILE + Math.floor(i / TILE) };
      }
    }
  }
  if (mask.base === 255) return { x: Math.floor(mask.width / 2), y: Math.floor(mask.height / 2) };
  return null; // 空选区，没有代表色
}

/**
 * 选取相似：把当前选区里"代表色"所在处当种子，对**整张图**做非连续魔棒
 * （复用 `magicWand`），再并入当前选区。
 *
 * 简化（已在完成报告披露）：真实 Photoshop 对选区内出现的每种颜色分别测
 * 一次再取并集；这里只用一个代表色（选区里第一个"确定选中"的像素），
 * 多色选区选取相似的覆盖面会比真实 Photoshop 窄。
 */
export async function similar(
  image: SourceImage,
  mask: SelectionMask,
  tolerance: number,
  options: OpOptions = {},
): Promise<SelectionMask> {
  throwIfAborted(options.signal);
  const seed = representativeSeed(mask);
  if (seed === null) return mask; // 空选区，没什么可扩展的
  const found = await magicWand(image, seed.x, seed.y, {
    tolerance,
    contiguous: false,
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });
  return union(mask, found);
}

/**
 * 扩大选区一轮：只看 `expand(mask,1)` 新长出来的那一圈候选像素——候选像素
 * 必然有个 4-邻域里已经选中的邻居（否则不会被 expand 1px 带出来），拿那个
 * 邻居的颜色跟候选像素的颜色比容差（跟魔棒同款 Oklab 距离），通过的才纳入。
 */
function growOnce(
  image: SourceImage,
  mask: SelectionMask,
  toleranceOklab: number,
): SelectionMask | null {
  const ring = subtract(expand(mask, 1), mask);
  const bounds = ring.contentBounds();
  if (bounds === null) return null; // 没有新候选，已经长不动了

  const { width, height, pixels } = image;
  const additions = new Map<string, Uint8Array>();
  let any = false;

  for (let y = bounds.y; y < bounds.y + bounds.height; y++) {
    for (let x = bounds.x; x < bounds.x + bounds.width; x++) {
      if (ring.valueAt(x, y) === 0) continue;
      const candidateOffset = (y * width + x) * 4;
      const neighbors: ReadonlyArray<readonly [number, number]> = [
        [x - 1, y],
        [x + 1, y],
        [x, y - 1],
        [x, y + 1],
      ];
      let matched = false;
      for (const [nx, ny] of neighbors) {
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
        if (mask.valueAt(nx, ny) <= 127) continue; // 只跟"确定已选中"的邻居比
        const neighborOffset = (ny * width + nx) * 4;
        const distance = oklab_distance_srgb8(
          pixels[neighborOffset]!,
          pixels[neighborOffset + 1]!,
          pixels[neighborOffset + 2]!,
          pixels[candidateOffset]!,
          pixels[candidateOffset + 1]!,
          pixels[candidateOffset + 2]!,
        );
        if (distance <= toleranceOklab) {
          matched = true;
          break;
        }
      }
      if (!matched) continue;
      any = true;
      const tx = Math.floor(x / TILE);
      const ty = Math.floor(y / TILE);
      const id = `${tx} ${ty}`;
      let tile = additions.get(id);
      if (tile === undefined) {
        const existing = mask.tile(tx, ty);
        tile = existing !== null ? Uint8Array.from(existing) : new Uint8Array(TILE * TILE).fill(mask.base);
        additions.set(id, tile);
      }
      tile[(y % TILE) * TILE + (x % TILE)] = 255;
    }
  }
  if (!any) return null;

  const tiles = new Map<string, Uint8Array>(additions);
  for (const { tx, ty } of mask.tileCoords()) {
    const id = `${tx} ${ty}`;
    if (!tiles.has(id)) tiles.set(id, mask.tile(tx, ty)!);
  }
  return SelectionMask.build(mask.width, mask.height, mask.base, tiles);
}

/** 扩大选区一轮最多能长出的候选环数——避免病态输入（比如全图都在容差内）卡死。 */
const GROW_MAX_ROUNDS = 64;

/**
 * 扩大选区（DESIGN.md 对比 Photoshop 补全第一档缺口 阶段 2）：反复把选区边缘
 * 向外长一圈，每个新像素跟它已选中的邻居比容差，直到长不动或到轮数上限。
 * 纯 TS 实现，不碰 wasm/Rust——只复用已有的 `oklab_distance_srgb8` 导出
 * （跟魔棒同一枚颜色距离函数，不重新发明）。
 */
export async function grow(
  image: SourceImage,
  mask: SelectionMask,
  tolerance: number,
  options: OpOptions = {},
): Promise<SelectionMask> {
  throwIfAborted(options.signal);
  await ensureKernel();
  const toleranceOklab = Math.min(255, Math.max(0, tolerance)) / 255;
  let current = mask;
  for (let round = 0; round < GROW_MAX_ROUNDS; round++) {
    throwIfAborted(options.signal);
    const next = growOnce(image, current, toleranceOklab);
    if (next === null) break;
    current = next;
  }
  return current;
}

/**
 * 色彩范围选取（选择→色彩范围…，DESIGN.md 对比 Photoshop 补全）：整图
 * 按跟参考色的 Oklab 距离给出**软选区**——距离越近选中程度越高，不是
 * 二值化阈值，这条线性衰减正是 PS"容差"滑杆调出来的那种边缘渐变选区
 * 手感的来源，也是它跟"魔棒非连续"/"选取相似"（两者都是硬边二值）的
 * 本质区别，不是同一个功能换个名字。
 *
 * `existing` 非空时限制在已有选区范围内（那里为 0 的像素结果也一定是
 * 0），同真实 Photoshop"色彩范围"尊重当前选区的规则——不传等于对整图
 * 生效。
 *
 * 简化（已在复盘记录披露）：只支持单个参考色（对话框里点一次画布取样），
 * 不支持真实 Photoshop 那样 Shift/Alt 点击继续加/减取样点、也没有"红色/
 * 黄色/高光/中间调/阴影/肤色"这类预设，也没有独立的"反相"以外的高级
 * 选项——这些都需要更多新 UI，先把"按颜色距离给软选区"这个核心能力做对。
 */
export async function colorRangeMask(
  image: SourceImage,
  refColor: readonly [number, number, number],
  fuzziness: number,
  existing: SelectionMask | null = null,
): Promise<SelectionMask> {
  await ensureKernel();
  const { width, height, pixels } = image;
  const toleranceOklab = Math.min(255, Math.max(1, fuzziness)) / 255;
  const [rr, rg, rb] = refColor;
  return rasterize(width, height, { x: 0, y: 0, width, height }, (x, y) => {
    if (existing !== null && existing.valueAt(x, y) === 0) return 0;
    const at = (y * width + x) * 4;
    const distance = oklab_distance_srgb8(rr, rg, rb, pixels[at]!, pixels[at + 1]!, pixels[at + 2]!);
    return Math.max(0, 1 - distance / toleranceOklab);
  });
}
